// AIAnalysingPipeline — shared "AI is working" animation for Hayah.
//
// Progress is tied to real elapsed time vs. a rolling-median ETA learned
// per feature (`aip.timings.<feature>` in localStorage). The bar
// asymptotes near ~97% so it never hits 100 before the result arrives.
//
// Source rows reflect the *actual* data being sent to the model: each AI
// generator calls publishSources(feature, sources) right before the
// fetch; the pipeline subscribes by feature and renders those labels in
// place of its hardcoded fallback.

import React, { useEffect, useRef, useState } from "react";
import { C, FM } from "../HayahToday";
import { subscribeSources } from "./pipelineSources";

const DEFAULT_SOURCES = ["Signals", "History", "Context"];
const DEFAULT_ETA_MS  = 4500;
const RING_SIZE       = 8;
const TICK_MS         = 100;
const MAX_PROGRESS    = 0.97;

function inferFeature(label) {
  const l = (label || "").toLowerCase();
  if (l.includes("last session"))     return "last-session";
  if (l.includes("readiness"))        return "readiness";
  if (l.includes("designing"))        return "workout-gen";
  if (l.includes("session"))          return "post-workout";
  if (l.includes("identifying food")) return "photo";
  if (l.includes("parsing meal"))     return "meal-parse";
  if (l.includes("plan"))             return "meal-plan";
  return "default";
}

function readETA(feature) {
  if (!feature) return DEFAULT_ETA_MS;
  try {
    const arr = JSON.parse(localStorage.getItem(`aip.timings.${feature}`) || "[]");
    if (!Array.isArray(arr) || !arr.length) return DEFAULT_ETA_MS;
    const sorted = [...arr].filter(n => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
    if (!sorted.length) return DEFAULT_ETA_MS;
    return sorted[Math.floor(sorted.length / 2)];
  } catch { return DEFAULT_ETA_MS; }
}

function recordETA(feature, dur) {
  if (!feature || !Number.isFinite(dur)) return;
  if (dur < 500 || dur > 60000) return;
  try {
    const key = `aip.timings.${feature}`;
    const arr = JSON.parse(localStorage.getItem(key) || "[]");
    arr.push(Math.round(dur));
    while (arr.length > RING_SIZE) arr.shift();
    localStorage.setItem(key, JSON.stringify(arr));
  } catch {}
}

function useTimedProgress(feature) {
  const startedRef = useRef(Date.now());
  const etaRef     = useRef(readETA(feature));
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    startedRef.current = Date.now();
    etaRef.current     = readETA(feature);
    setElapsed(0);
    const id = setInterval(() => setElapsed(Date.now() - startedRef.current), TICK_MS);
    return () => {
      clearInterval(id);
      recordETA(feature, Date.now() - startedRef.current);
    };
  }, [feature]);

  const eta = Math.max(1500, etaRef.current);
  const raw = elapsed / eta;
  const progress = raw >= MAX_PROGRESS
    ? MAX_PROGRESS + (0.99 - MAX_PROGRESS) * (1 - Math.exp(-(raw - MAX_PROGRESS)))
    : raw;
  const pct  = Math.max(1, Math.min(99, Math.round(progress * 100)));
  const phase = progress < 0.40 ? "gather"
              : progress < 0.70 ? "scan"
                                : "compute";
  return { progress, pct, phase, eta };
}

function useLiveSources(feature, fallback) {
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (!feature) return;
    return subscribeSources(feature, setLive);
  }, [feature]);
  return (live && live.length) ? live : fallback;
}

export default function AIAnalysingPipeline({
  label = "Analysing",
  computeLabel,
  sources = DEFAULT_SOURCES,
  variant = "card",
  accent = "teal",
  theme,
  feature,
}) {
  const accentColor = accent === "amber" ? (theme?.amber || C.amber) : (theme?.teal || C.teal);
  const dim   = theme?.ink3 || theme?.dim || C.dim;
  const faint = theme?.ink4 || theme?.faint || C.faint;
  const rule  = theme?.lineSoft || theme?.line || C.rule;

  const compVerb = computeLabel ||
    (/generat|synth|plan/i.test(label) ? "Synthesising" : "Computing assessment");

  const resolvedFeature = feature || inferFeature(label);
  const { progress, pct, phase } = useTimedProgress(resolvedFeature);
  const displaySources = useLiveSources(resolvedFeature, sources);

  const N = displaySources.length;
  const ticks = Math.min(N, Math.floor((progress / 0.40) * N));

  if (variant === "inline") {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FM, fontSize: "0.95em", letterSpacing: "0.02em" }}>
        <Dots color={accentColor} />
        <span style={{ color: "inherit", opacity: 0.95 }}>
          {phase === "compute" ? compVerb : label}
        </span>
        <span style={{ fontVariantNumeric: "tabular-nums", opacity: 0.7 }}>{pct}%</span>
        <Scope />
      </span>
    );
  }

  return (
    <div style={{
      position: "relative", overflow: "hidden",
      borderRadius: 12,
      padding: "12px 14px",
      background: `linear-gradient(180deg, ${accentColor}0e, ${accentColor}04)`,
      border: `1px solid ${accentColor}33`,
      fontFamily: FM,
    }}>
      <Scope />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Dots color={accentColor} />
          <span style={{ fontSize: 10.5, color: accentColor, fontWeight: 700, letterSpacing: "1.4px", textTransform: "uppercase" }}>
            {phase === "compute" ? compVerb : label}
          </span>
        </div>
        <span style={{ fontSize: 9.5, color: faint, letterSpacing: "1px", fontVariantNumeric: "tabular-nums" }}>
          {pct.toString().padStart(2, "0")}%
        </span>
      </div>

      <div style={{ position: "relative", height: 4, borderRadius: 4, background: `${accentColor}1a`, overflow: "hidden", marginBottom: 12 }}>
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor}cc)`,
          boxShadow: `0 0 6px ${accentColor}88`,
          transition: `width ${TICK_MS}ms linear`,
        }} />
      </div>

      <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 5 }}>
        {displaySources.map((src, i) => {
          const isIn   = ticks > i || phase !== "gather";
          const isTick = ticks > i || phase !== "gather";
          return (
            <div key={`${i}-${src}`} style={{
              display: "flex", alignItems: "center", gap: 9,
              opacity: isIn ? 1 : 0,
              transform: isIn ? "translateX(0)" : "translateX(-6px)",
              transition: "opacity 0.22s ease, transform 0.22s ease",
              animation: isIn ? `aip-row-in 0.32s ${i * 0.04}s ease both` : "none",
            }}>
              <span style={{
                width: 10, height: 10, position: "relative",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>
                {isTick ? (
                  <span style={{
                    width: 6, height: 6, borderRadius: 6,
                    background: accentColor,
                    boxShadow: `0 0 6px ${accentColor}`,
                    animation: "aip-tick 0.32s ease",
                  }} />
                ) : (
                  <span style={{
                    width: 6, height: 6, borderRadius: 6,
                    border: `1px solid ${accentColor}66`,
                    animation: "aip-row-pulse 1s ease-in-out infinite",
                  }} />
                )}
              </span>
              <span style={{ fontSize: 11, color: isTick ? dim : faint, letterSpacing: "0.4px", flex: 1, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={src}>
                {src}
              </span>
              <span style={{
                fontSize: 9, color: isTick ? accentColor : faint,
                letterSpacing: "1.1px",
                fontVariantNumeric: "tabular-nums",
                opacity: isTick ? 1 : 0.5,
                transition: "opacity 0.2s ease, color 0.2s ease",
                flexShrink: 0,
              }}>
                {phase === "gather" ? (isTick ? "OK" : "··") :
                 phase === "scan"   ? "SCAN" :
                                      "✓"}
              </span>
            </div>
          );
        })}

        {phase === "scan" && (
          <div style={{
            position: "absolute", left: 0, right: 0, top: 0, bottom: 0,
            pointerEvents: "none", overflow: "hidden",
          }}>
            <div style={{
              position: "absolute", left: 0, right: 0,
              height: 24,
              background: `linear-gradient(180deg, transparent, ${accentColor}38, transparent)`,
              animation: "aip-sweep 1.1s linear infinite",
              mixBlendMode: "screen",
            }} />
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${rule}` }}>
        <span style={{ fontSize: 9, color: faint, letterSpacing: "1.2px" }}>STREAM</span>
        <div style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 3, height: 14 }}>
          {Array.from({ length: 18 }).map((_, i) => (
            <div key={i} style={{
              flex: 1,
              background: phase === "compute" ? accentColor : `${accentColor}66`,
              borderRadius: 1,
              height: 14,
              transformOrigin: "bottom",
              animation: phase === "compute"
                ? `aip-bar-pulse 0.9s ease-in-out ${i * 0.05}s infinite`
                : `aip-bar-idle 1.6s ease-in-out ${i * 0.04}s infinite`,
            }} />
          ))}
        </div>
        <span style={{ fontSize: 9, color: accentColor, letterSpacing: "1.2px", fontVariantNumeric: "tabular-nums" }}>
          {phase === "compute" ? "EXEC" : phase === "scan" ? "SCAN" : "IDLE"}
        </span>
      </div>
    </div>
  );
}

function Dots({ color }) {
  return (
    <span style={{ display: "inline-flex", gap: 3 }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 4, height: 4, borderRadius: 4,
          background: color,
          animation: `aip-dot 1.1s ease-in-out ${i * 0.16}s infinite`,
        }} />
      ))}
    </span>
  );
}

function Scope() {
  return (
    <style>{`
      @keyframes aip-row-in    { from { opacity: 0; transform: translateX(-6px) } to { opacity: 1; transform: translateX(0) } }
      @keyframes aip-row-pulse { 0%,100% { opacity: 0.4 } 50% { opacity: 1 } }
      @keyframes aip-tick      { 0% { transform: scale(0.4); opacity: 0 } 60% { transform: scale(1.25); opacity: 1 } 100% { transform: scale(1); opacity: 1 } }
      @keyframes aip-sweep     { 0% { top: -24px } 100% { top: 100% } }
      @keyframes aip-bar-pulse { 0%,100% { transform: scaleY(0.35) } 50% { transform: scaleY(1) } }
      @keyframes aip-bar-idle  { 0%,100% { transform: scaleY(0.25) } 50% { transform: scaleY(0.55) } }
      @keyframes aip-dot       { 0%,100% { opacity: 0.25; transform: scale(0.8) } 50% { opacity: 1; transform: scale(1.15) } }
    `}</style>
  );
}
